import { Alert } from '@ordo/ui/alert'
import { Calendar } from '@ordo/ui/calendar'
import { cn } from '@ordo/ui/cn'
import { Popover, PopoverContent, PopoverTrigger } from '@ordo/ui/popover'
import { RadioGroup, RadioGroupItem } from '@ordo/ui/radio-group'
import { SearchableSelect, type SearchableSelectOption } from '@ordo/ui/searchable-select'
import { Textarea } from '@ordo/ui/textarea'
import { AlertTriangle, ArrowRight, CalendarIcon, Check, Clock } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import type { DateRange as PickerDateRange } from 'react-day-picker'

import { apiFetch } from '@/api/http'
import { useCreateSubstitutionRequestMutation } from '@/api/substitution-requests'
import { Button } from '@/components/chefs/button'
import { CHIP_CLASS_LG_PRIMARY, CHIP_CLASS_SM, CHIP_CLASS_SM_PRIMARY, ChipButton } from '@/components/chefs/chip-button'
import { findActiveByIngredient } from '@/lib/chefs/substitution-lookup'
import type { Substitution } from '@/lib/chefs/substitutions'

export interface IngredientOption {
  key: string
  name: string
  unit: string
}

interface FormProps {
  kitchenLocationId: string | null
  ingredientOptions: IngredientOption[]
  lockedIngredient: IngredientOption | null
  defaultStartDate: Date
  defaultEndDate: Date
  existingSubstitutions: Substitution[]
  onSeeAll: () => void
  onSubmitted: () => void
  onCancel: () => void
}

function toIsoDate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function formatRange(start: Date | undefined, end: Date | undefined): string {
  if (!start) {
    return 'Pick dates'
  }
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  if (!end || end.getTime() === start.getTime()) {
    return fmt(start)
  }
  return `${fmt(start)} – ${fmt(end)}`
}

function formatIsoRange(startIso: string, endIso: string): string {
  const start = new Date(`${startIso}T00:00:00`)
  const end = new Date(`${endIso}T00:00:00`)
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  if (startIso === endIso) {
    return fmt(start)
  }
  return `${fmt(start)} – ${fmt(end)}`
}

function toSelectOptions(ingredients: IngredientOption[]): SearchableSelectOption[] {
  return ingredients.map((ing) => ({
    value: ing.key,
    label: ing.name,
    searchText: `${ing.name} ${ing.unit}`,
  }))
}

export function SubstitutionModalForm({
  kitchenLocationId,
  ingredientOptions,
  lockedIngredient,
  defaultStartDate,
  defaultEndDate,
  existingSubstitutions,
  onSeeAll,
  onSubmitted,
  onCancel,
}: FormProps) {
  const createMutation = useCreateSubstitutionRequestMutation()
  const [selectedKey, setSelectedKey] = useState<string | null>(lockedIngredient?.key ?? null)
  const [replacement, setReplacement] = useState('')
  /** Set only when the replacement was picked from the catalog. */
  const [substituteIngredientId, setSubstituteIngredientId] = useState<string | null>(null)
  const [replacementOptions, setReplacementOptions] = useState<SearchableSelectOption[]>([])
  const [reason, setReason] = useState('')
  const [range, setRange] = useState<PickerDateRange>({ from: defaultStartDate, to: defaultEndDate })
  const [submitting, setSubmitting] = useState(false)
  const [overrideAcknowledged, setOverrideAcknowledged] = useState(false)
  const [overrideChoice, setOverrideChoice] = useState<'yes' | 'no' | null>(null)

  const selected =
    lockedIngredient ?? (selectedKey ? (ingredientOptions.find((i) => i.key === selectedKey) ?? null) : null)

  const existingForSelected = useMemo<Substitution | null>(() => {
    if (!selected) {
      return null
    }
    return findActiveByIngredient(existingSubstitutions, selected.name, selected.unit)
  }, [existingSubstitutions, selected])

  const needsOverrideConfirmation = !!existingForSelected && !overrideAcknowledged

  const valid =
    !!selected &&
    !needsOverrideConfirmation &&
    substituteIngredientId !== null &&
    reason.trim().length > 0 &&
    !!range.from &&
    !!range.to &&
    !submitting

  const searchIngredients = useCallback(async (query: string): Promise<SearchableSelectOption[]> => {
    const payload = await apiFetch<{ ingredients: Array<{ id: string; name: string }> }>('/api/catalog/ingredients', {
      query: { search: query, ingredientType: 'FOOD', limit: 25 },
    })
    const options = payload.ingredients.map((row) => ({ value: row.id, label: row.name }))
    setReplacementOptions(options)
    return options
  }, [])

  function clearForm() {
    if (lockedIngredient) {
      onCancel()
      return
    }
    setSelectedKey(null)
    setReplacement('')
    setSubstituteIngredientId(null)
    setReason('')
    setRange({ from: defaultStartDate, to: defaultEndDate })
    setOverrideAcknowledged(false)
    setOverrideChoice(null)
  }

  function handleSelectIngredient(key: string | null) {
    setSelectedKey(key)
    setOverrideAcknowledged(false)
    setOverrideChoice(null)
  }

  function handleConfirmOverrideChoice() {
    if (overrideChoice === 'yes') {
      setOverrideAcknowledged(true)
    } else if (overrideChoice === 'no') {
      clearForm()
    }
  }

  async function handleSubmit() {
    if (!valid || !selected || !range.from || !range.to || !kitchenLocationId) {
      return
    }
    setSubmitting(true)
    try {
      await createMutation.mutateAsync({
        kitchenLocationId,
        ingredientName: selected.name,
        ingredientUnit: selected.unit,
        replacement: replacement.trim(),
        substituteIngredientId,
        reason: reason.trim(),
        // Buy American is NOT asked of chefs: nobody at a bench knows the FAR
        // exception type or the domestic commodity percent, and the answer is
        // already on the brand option they picked. It resolves from the
        // catalog and is confirmed by the ORDO_ADMIN who approves the request
        // (`buyAmericanReviewedByUserId` exists for exactly that).
        startDate: toIsoDate(range.from),
        endDate: toIsoDate(range.to),
      })
      onSubmitted()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      className="flex min-h-0 flex-1 flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        void handleSubmit()
      }}>
      <Alert variant="warning" className="flex shrink-0 items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
        <div>
          <div className="font-medium">Substitutions must be approved</div>
          <div className="text-sm opacity-90">
            To ensure compliance with meal program standards and allergy protocols,{' '}
            <span className="font-semibold">
              no ingredient substitutions may be made without prior written approval from the Ordo team
            </span>
            .
          </div>
        </div>
      </Alert>

      <div className="-mx-1 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-1">
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground">Ingredient</span>
          {lockedIngredient ? (
            <div className="flex items-baseline gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
              <span className="font-medium text-foreground">{lockedIngredient.name}</span>
              <span className="text-xs text-muted-foreground">{lockedIngredient.unit}</span>
            </div>
          ) : (
            <SearchableSelect
              multi={false}
              value={selectedKey}
              onChange={handleSelectIngredient}
              options={toSelectOptions(ingredientOptions)}
              placeholder="Search ingredients…"
            />
          )}
        </div>

        {existingForSelected ? (
          <div className="flex flex-col gap-3 rounded-md bg-muted px-3 py-3 text-sm">
            <div className="font-medium text-foreground">
              {needsOverrideConfirmation
                ? 'This ingredient already has a substitution'
                : 'If approved, this will override the substitution below'}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-border bg-background px-3 py-2">
              <span className="text-muted-foreground line-through">{existingForSelected.ingredientName}</span>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
              <span className="font-medium text-foreground">{existingForSelected.replacement}</span>
              {existingForSelected.status === 'approved' ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-800">
                  <Check className="-ml-0.5 mr-0.5 h-3 w-3" strokeWidth={2.5} />
                  Substitution approved
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                  <Clock className="-ml-0.5 mr-0.5 h-3 w-3" strokeWidth={2.5} />
                  Pending review
                </span>
              )}
              <span className="ml-auto text-xs text-muted-foreground">
                {formatIsoRange(existingForSelected.startDate, existingForSelected.endDate)}
              </span>
            </div>
            {needsOverrideConfirmation ? (
              <>
                <div className="text-foreground">
                  Submitting another request will override the existing substitution if approved. Continue?
                </div>
                <RadioGroup
                  value={overrideChoice ?? ''}
                  onValueChange={(v) => setOverrideChoice(v as 'yes' | 'no')}
                  className="gap-1.5">
                  <label
                    htmlFor="sub-override-yes"
                    className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
                    <RadioGroupItem id="sub-override-yes" value="yes" />
                    Yes, request a new substitution
                  </label>
                  <label
                    htmlFor="sub-override-no"
                    className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
                    <RadioGroupItem id="sub-override-no" value="no" />
                    No, cancel
                  </label>
                </RadioGroup>
                <div className="pt-2">
                  <ChipButton
                    variant="default"
                    className={CHIP_CLASS_SM_PRIMARY}
                    disabled={overrideChoice === null}
                    onClick={handleConfirmOverrideChoice}>
                    Confirm
                  </ChipButton>
                </div>
              </>
            ) : (
              <div>
                <ChipButton className={CHIP_CLASS_SM} onClick={clearForm}>
                  Cancel & keep substitution
                </ChipButton>
              </div>
            )}
          </div>
        ) : null}

        {needsOverrideConfirmation ? null : (
          <>
            <div className="flex flex-col gap-1.5">
              {/* SearchableSelect renders its own trigger and takes no id, so
                  htmlFor would point at nothing now the text input is gone. */}
              <p className="text-sm font-medium text-foreground">
                What ingredient would you like to use as a replacement?
              </p>
              {/* Catalog only. Chefs don't add to the catalog, and a typed
                  name carries no ids — so crediting and nutrition could never
                  follow it, which is the whole point of recording the swap. */}
              <SearchableSelect
                placeholder="Search our ingredients"
                options={replacementOptions}
                value={substituteIngredientId}
                onRemoteSearch={searchIngredients}
                onChange={(value) => {
                  setSubstituteIngredientId(value)
                  // The stored text always mirrors the chosen row, so the label
                  // and the id can never disagree.
                  setReplacement(replacementOptions.find((option) => option.value === value)?.label ?? '')
                }}
              />
              {substituteIngredientId ? (
                <p className="text-xs text-muted-foreground">
                  Crediting and nutrition will follow this choice once approved.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Don&apos;t see it? It isn&apos;t in our catalog yet — ask the Ordo team to add it.
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="sub-reason" className="text-sm font-medium text-foreground">
                What is the reason for requesting this substitution?
              </label>
              <Textarea
                id="sub-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason"
                rows={3}
                disabled={submitting}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-foreground">
                How long do you anticipate needing to use this substitution?
              </span>
              <Popover>
                <PopoverTrigger asChild disabled={submitting}>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={submitting}
                    className={cn('justify-start gap-2 font-normal', !range.from && 'text-muted-foreground')}>
                    <CalendarIcon className="h-4 w-4" />
                    {formatRange(range.from, range.to)}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={range}
                    onSelect={(r) => setRange(r ?? { from: undefined, to: undefined })}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </>
        )}
      </div>

      {needsOverrideConfirmation ? null : (
        <div className="flex shrink-0 items-center justify-between gap-2 pt-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onSeeAll}
            disabled={submitting}
            className="text-sm text-muted-foreground hover:text-foreground">
            See all substitutions
          </Button>
          <ChipButton type="submit" variant="default" className={CHIP_CLASS_LG_PRIMARY} disabled={!valid}>
            {submitting ? 'Submitting…' : 'Submit request'}
          </ChipButton>
        </div>
      )}
    </form>
  )
}
